import { forwardRef, useEffect, useImperativeHandle, useMemo, useState, type MutableRefObject } from 'react';
import * as THREE from 'three';
import { MapPin, ChevronDown, ChevronUp } from 'lucide-react';
import { useI18n } from '../../i18n';
import { parseScenePins, type ScenePinsAsset, type PinObserver } from '../../types/scenePins';
import { convertZUpToYUp, pickScenePin, pinObserverToWorldView, frameScenePinGroup } from '../../utils/measureUtils';
import { resolveAssetUrl } from '../../utils/assetUrl';
import type { SparkViewer } from '../../utils/SparkViewer';

export interface ScenePinsLayerHandle {
  /** Navigate to a section tag name (e.g. "section 83") or individual pin name (e.g. "S83-0001"). */
  navigateTo: (name: string) => void;
  /** Set tag filters, e.g. { Direction: "north" } or { Section: "section 83" }. Pass {} to clear. */
  applyFilters: (filters: Record<string, string>) => void;
}

interface Props {
  viewerRef: MutableRefObject<unknown>;
  sources: string[];
  source: string;
  onSourceChange: (url: string) => void;
  interactive: boolean;
  hideControls?: boolean;
  pinNames?: string[];
  onAssetReady?: (asset: ScenePinsAsset) => void;
  onPinSelect?: (name: string) => void;
}

/** One batched Points draw call, independent of existing POIs and source imagery. */
const ScenePinsLayer = forwardRef<ScenePinsLayerHandle, Props>(function ScenePinsLayer({ viewerRef, sources, source, onSourceChange, interactive, hideControls, pinNames, onAssetReady, onPinSelect }, ref) {
  const { t } = useI18n();
  const labels = t.pins;
  const [asset, setAsset] = useState<ScenePinsAsset | null>(null);
  const [failed, setFailed] = useState(false);
  const [retry, setRetry] = useState(0);
  const [open, setOpen] = useState(true);
  const [visible, setVisible] = useState(true);
  const [size, setSize] = useState(7);
  const [opacity, setOpacity] = useState(0.9);
  const [colorGroup, setColorGroup] = useState('');
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<number | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setAsset(null); setFailed(false); setSelected(null); setFilters({}); setColorGroup('');
    setVisible(true); setOpen(true);
    (async () => {
      try {
        const response = await fetch(resolveAssetUrl(source)!, { signal: controller.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const parsed = parseScenePins(await response.json());
        if (!controller.signal.aborted) {
          setAsset(parsed);
          onAssetReady?.(parsed);
          setColorGroup(parsed.tag_groups[0]?.name ?? '');
        }
      } catch (error) {
        if (!controller.signal.aborted) { console.error('[ScenePins]', error); setFailed(true); }
      }
    })();
    return () => controller.abort();
  }, [source, retry, onAssetReady]);

  const positions = useMemo(() => asset?.pins.map(pin => convertZUpToYUp({
    x: pin.position[0], y: pin.position[1], z: pin.position[2],
  })) ?? [], [asset]);
  const allowedNames = useMemo(() => pinNames ? new Set(pinNames) : null, [pinNames]);
  const indices = useMemo(() => asset?.pins.flatMap((pin, i) =>
    (!allowedNames || allowedNames.has(pin.name)) && (hideControls || Object.values(filters).every(tag => !tag || pin.tags.includes(tag))) ? [i] : []) ?? [], [asset, filters, allowedNames, hideControls]);
  const filteredPositions = useMemo(() => indices.map(i => positions[i]), [indices, positions]);

  useEffect(() => { setSelected(null); }, [filters]);

  useEffect(() => {
    if (!asset || (!visible && !hideControls)) return;
    const viewer = viewerRef.current as SparkViewer | null;
    const scene = viewer?.scene;
    const canvas = viewer?.renderer?.domElement;
    if (!viewer || !scene || !canvas) return;
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(filteredPositions.flatMap(p => [p.x, p.y, p.z]), 3));
    const group = hideControls ? undefined : asset.tag_groups.find(g => g.name === colorGroup);
    const palette = new Map(group?.tags.map((tag, i) => [tag.name, new THREE.Color().setHSL((i * 0.61803398875) % 1, 0.8, 0.6)]) ?? []);
    const base = new THREE.Color('#fb923c');
    const selectedColor = new THREE.Color('#ffffff');
    const colors = indices.flatMap(i => {
      const color = i === selected ? selectedColor : asset.pins[i].tags.map(tag => palette.get(tag)).find(Boolean) ?? base;
      return [color.r, color.g, color.b];
    });
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    const markerCanvas = document.createElement('canvas');
    markerCanvas.width = markerCanvas.height = 64;
    const ctx = markerCanvas.getContext('2d')!;
    ctx.beginPath(); ctx.arc(32, 32, 29, 0, Math.PI * 2); ctx.fillStyle = 'white'; ctx.fill();
    ctx.lineWidth = 6; ctx.strokeStyle = '#111111'; ctx.stroke();
    const texture = new THREE.CanvasTexture(markerCanvas);
    const material = new THREE.PointsMaterial({ size, sizeAttenuation: false, vertexColors: true,
      map: texture, transparent: true, opacity, alphaTest: 0.1, depthTest: true, depthWrite: false, toneMapped: false });
    const points = new THREE.Points(geometry, material);
    points.name = `si-pins:${asset.name}`;
    points.renderOrder = 10;
    scene.add(points);

    // Capture only actual pin clicks; camera drags and other tool interactions remain available.
    let down: { x: number; y: number } | null = null;
    const pointerDown = (event: PointerEvent) => { if (event.button === 0) down = { x: event.clientX, y: event.clientY }; };
    const pointerUp = (event: PointerEvent) => {
      const start = down; down = null;
      if (!interactive || !start || Math.abs(event.clientX - start.x) + Math.abs(event.clientY - start.y) > 5) return;
      const picked = pickScenePin(filteredPositions, viewer.camera, canvas.getBoundingClientRect(), event.clientX, event.clientY, Math.max(size / 2, 5));
      if (picked !== null) { setSelected(indices[picked]); setOpen(true); onPinSelect?.(asset.pins[indices[picked]].name); }
    };
    const doubleClick = (event: MouseEvent) => {
      if (interactive && pickScenePin(filteredPositions, viewer.camera, canvas.getBoundingClientRect(), event.clientX, event.clientY, Math.max(size / 2, 5)) !== null) {
        event.stopImmediatePropagation();
      }
    };
    canvas.addEventListener('pointerdown', pointerDown, true);
    canvas.addEventListener('pointerup', pointerUp, true);
    canvas.addEventListener('dblclick', doubleClick, true);
    return () => {
      canvas.removeEventListener('pointerdown', pointerDown, true);
      canvas.removeEventListener('pointerup', pointerUp, true);
      canvas.removeEventListener('dblclick', doubleClick, true);
      scene.remove(points); geometry.dispose(); material.dispose(); texture.dispose();
    };
  }, [asset, filteredPositions, indices, visible, size, opacity, colorGroup, selected, viewerRef, interactive, onPinSelect, hideControls]);

  const goToView = (observer: PinObserver) => {
    void (viewerRef.current as SparkViewer | null)?.teleportTo(pinObserverToWorldView(observer));
  };

  useImperativeHandle(ref, () => ({
    navigateTo(name) {
      if (!asset) { console.warn('[ScenePins] Navigation skipped: pins not loaded', name); return; }
      const lower = name.trim().toLowerCase().replace(/[–—]/g, '-').replace(/\s+stand$/, '')
        .replace(/^(north|south)[ -]?(east|west)$/, '$1-$2');
      console.info('[ScenePins] Navigation requested', { name, target: lower });

      // 1. Section tag with its own observer (e.g. "section 83")
      for (const group of asset.tag_groups) {
        const tag = group.tags.find(t => typeof t === 'object' && t.name.toLowerCase() === lower);
        if (typeof tag === 'object' && tag?.observer) { goToView(tag.observer); return; }
      }

      // 2. Individual pin by name (e.g. "S83-0001")
      const namedPin = asset.pins.find(p => p.name.toLowerCase() === lower);
      if (namedPin?.observer) { setSelected(asset.pins.indexOf(namedPin)); goToView(namedPin.observer); return; }

      // Frame the entire direction/color group rather than borrowing one section's camera.
      const viewer = viewerRef.current as SparkViewer | null;
      if (!viewer) { console.warn('[ScenePins] Navigation skipped: viewer unavailable'); return; }
      const matchingPositions = asset.pins.flatMap((pin, i) =>
        pin.tags.some(tag => tag.toLowerCase() === lower) ? [positions[i]] : []);
      const view = frameScenePinGroup(matchingPositions, positions, viewer.camera);
      if (view) {
        console.info('[ScenePins] Framing group', { target: lower, pins: matchingPositions.length, view });
        setSelected(null); void viewer.teleportTo(view);
      } else console.warn('[ScenePins] No matching navigation target', { name, target: lower });
    },
    applyFilters(filters) {
      setFilters(filters);
      setOpen(true);
    },
  }), [asset, positions]); // eslint-disable-line react-hooks/exhaustive-deps
  const pin = selected === null ? null : asset?.pins[selected];
  const control = 'w-full rounded border border-white/20 bg-[#20242a] px-2 py-1 text-xs text-white';

  if (hideControls) return null;

  return <aside className="absolute left-4 top-4 z-[1000] w-72 rounded-xl border border-white/15 bg-[#11151b]/95 text-white shadow-xl" aria-label={labels.assets}>
    <button className="flex w-full items-center gap-2 p-3 text-left text-sm font-semibold" onClick={() => setOpen(!open)} aria-expanded={open}>
      <MapPin size={16} className="text-orange-300" /><span className="min-w-0 flex-1 truncate">{asset?.name ?? labels.assets}</span>
      {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
    </button>
    {open && <div className="max-h-[70vh] space-y-3 overflow-y-auto border-t border-white/10 p-3 text-xs">
      {sources.length > 1 && <label className="block">{labels.assets}<select className={control} value={source} onChange={e => onSourceChange(e.target.value)}>
        {sources.map(url => <option key={url} value={url}>{decodeURIComponent(url.split(/[?#]/)[0].split('/').pop() ?? url)}</option>)}
      </select></label>}
      {failed ? <div role="alert"><p>{labels.failed} {labels.invalid}</p><button className="mt-2 underline" onClick={() => setRetry(n => n + 1)}>{labels.retry}</button></div>
        : !asset ? <p role="status">{labels.loading}</p> : <>
          <p className="text-white/60">{asset.description}</p>
          <p className="font-semibold text-orange-200" aria-live="polite">{labels.count(indices.length, asset.pins.length)}</p>
          <label className="flex items-center gap-2"><input type="checkbox" checked={visible} onChange={e => setVisible(e.target.checked)} />{labels.visible}</label>
          <label className="block">{labels.size}: {size}px<input className="w-full" type="range" min="3" max="20" value={size} onChange={e => setSize(+e.target.value)} /></label>
          <label className="block">{labels.opacity}: {Math.round(opacity * 100)}%<input className="w-full" type="range" min="0.1" max="1" step="0.05" value={opacity} onChange={e => setOpacity(+e.target.value)} /></label>
          <label className="block">{labels.colorBy}<select className={control} value={colorGroup} onChange={e => setColorGroup(e.target.value)}>
            <option value="">{labels.uniform}</option>{asset.tag_groups.map(g => <option key={g.name}>{g.name}</option>)}
          </select></label>
          {asset.tag_groups.map(group => {
            const current = group.tags.find(tag => tag.name === filters[group.name]);
            return <div key={group.name}>
              <label className="block" title={group.description}>{group.name}<select className={control} value={filters[group.name] ?? ''} onChange={e => setFilters(f => ({ ...f, [group.name]: e.target.value }))}>
                <option value="">{labels.all}</option>{group.tags.map(tag => <option key={tag.name}>{tag.name}</option>)}
              </select></label>
              {current?.observer && <button className="mt-1 text-orange-200 underline" onClick={() => goToView(current.observer!)}>{labels.view}</button>}
            </div>;
          })}
          <button className="underline text-white/60" onClick={() => setFilters({})}>{labels.reset}</button>
          {pin ? <section className="space-y-2 border-t border-white/15 pt-3">
            <p className="text-white/50">{labels.selected}</p><h3 className="font-semibold">{pin.name}</h3><p>{pin.description}</p>
            <p className="text-orange-200">{pin.tags.join(' · ')}</p>
            {pin.observer && <button className="underline" onClick={() => goToView(pin.observer!)}>{labels.view}</button>}
          </section> : <p className="text-white/50">{labels.selectHint}</p>}
        </>}
    </div>}
  </aside>;
});

export default ScenePinsLayer;
